/**
 * Bounded auth transport for the pro admin frontend (KB-3033).
 *
 * Ported from marketing's `src/lib/auth.ts` `raceWithBoundedTimeout` (KB-2500),
 * following the KB-2606 precedent for porting a marketing helper into pro
 * (`src/lib/firestore-transport.ts`, ported from marketing's
 * `src/lib/firebase-firestore.ts`).
 *
 * WHY a bound is needed at all — a Firebase Auth request can hang FOREVER, and
 * nothing upstream will ever reject it. The chain, verified against this
 * worktree's `node_modules` at `firebase@12.16.0`:
 *
 *   `_performApiRequest` (@firebase/auth `index-d90d2ee5.js:891`) awaits
 *   `auth._getAdditionalHeaders()` at `:909` BEFORE issuing the fetch →
 *   `_getAdditionalHeaders` (`:3165`) awaits `this._getAppCheckToken()` at
 *   `:3183` on EVERY auth request → that awaits the App Check provider's
 *   `getToken()` → `@firebase/app-check` `getToken$1` awaits
 *   `reCAPTCHAState.initialized.promise` (`index.cjs.js:1154`) → that Deferred
 *   is resolved ONLY from `script.onload` in `loadReCAPTCHAV3Script` (`:1191`,
 *   `script.onload` at `:1194`/`:1201`), which sets **no `script.onerror`**.
 *
 * So if `recaptcha/api.js` fails to load, the Deferred never settles, and every
 * auth request that reaches `_performApiRequest` — password sign-in, the popup
 * flow's origin check and IdP exchange, forced token refresh — hangs silently
 * and permanently.
 *
 * NOT sign-out, though an earlier draft of this comment said so. `AuthImpl.signOut()`
 * (`index-d90d2ee5.js:2895`) runs the `beforeStateQueue` middleware and clears
 * persistence; it never calls `_performApiRequest`, so it awaits no App Check
 * token and cannot hang this way. Corrected after round-1 review found it: this
 * file is the cited evidence for the whole design, so an unverified claim
 * sitting in it is the very defect this ticket exists to fix.
 *
 * The SDK's own docstring at `index.cjs.js:697` reads "This
 * function always resolves", which is a claim about *rejection*, not about
 * *settling*: the promise it returns is built as
 * `new Promise((resolve, _reject) => …)` with `_reject` never called, so it has
 * no rejection path at all. A try/catch cannot help. Only a race can.
 *
 * This is upstream SDK shape and is explicitly not ours to patch (KB-3033 scope);
 * the bound lives here, at our own boundary.
 */

import type { AppCheckOutcome } from './appCheckOutcome';

/**
 * How long any single auth request may take before we stop waiting.
 *
 * 15 s matches `ForgotPasswordPage.tsx`'s `resetTimeoutMs = 15_000`, which
 * KB-2361 chose to bound the *same* class of failure (an enforced-App-Check
 * attempt whose promise never settles) on the adjacent auth form. Matching it
 * is deliberate: two bounds on two auth forms in one product that disagree
 * about how long "too long" is would be a difference with no reason behind it.
 *
 * It is deliberately generous. A false trip over what is merely a slow
 * connection ends a sign-in the user must then restart by hand, which is worse
 * than waiting a few extra seconds. (Since KB-3352 a false trip no longer also
 * sends them hunting for a content blocker: the copy follows the App Check
 * outcome, and only a missing `grecaptcha` earns that diagnosis.) Exported so tests reference the real value
 * instead of a duplicated magic number (same convention as
 * `BOOTSTRAP_BUDGET_MS` in `src/context/AuthContext.tsx`).
 */
export const AUTH_REQUEST_BUDGET_MS = 15000;

/**
 * How long an auth await that WAITS ON A HUMAN may take (KB-3033 round-1 #3).
 *
 * `signInWithPopup` is not a request — it is a request, then a person reading a
 * Google account chooser and possibly typing a password, then a second request.
 * Verified in `firebase@12.16.0`: the promise spans an App Check-gated call on
 * BOTH sides of the human. Before the popup opens, `_validateOrigin`
 * (`index-d90d2ee5.js:10185`) → `_getProjectConfig` (`:10163`) →
 * `_performApiRequest`; after it closes, `signInWithIdp` (`:4200`) →
 * `_performSignInRequest` (`:985`) → `_performApiRequest`. So the hang is
 * genuinely reachable, but so is a perfectly healthy two-minute sign-in.
 *
 * Bounding that at `AUTH_REQUEST_BUDGET_MS` would therefore break WORKING
 * logins: a user who takes 16 s to pick an account would be told their sign-in
 * timed out. That is the false-trip failure this module already warns about,
 * except guaranteed rather than unlucky.
 *
 * 120 s is chosen to sit comfortably above human account-selection time, not
 * derived from a measurement — stating that plainly because this file's own
 * standard is not to dress a judgement call as evidence. It preserves the
 * property that actually matters: the button can never stick FOREVER.
 */
export const AUTH_INTERACTIVE_BUDGET_MS = 120000;

/**
 * Thrown when a bounded auth await exceeds its budget. Distinguishable from
 * every Firebase error by `code`, so callers can render timeout copy chosen by
 * the App Check outcome rather than a generic failure message (KB-3033 AC-4,
 * KB-3352 AC-2).
 *
 * `code` deliberately does NOT start with `auth/`: `handleFirebaseError`
 * (`src/utils/errorUtils.ts`) switches on that prefix and would fall through to
 * `Authentication error: <message>`, which is exactly the generic phrasing
 * AC-1 and AC-4 exist to eliminate.
 *
 * The message names no cause. It used to end "the App Check reCAPTCHA token
 * mint is the usual cause", and it is the TITLE of the Sentry event — so every
 * recurrence arrived pre-attributed to a mechanism nothing had observed. The
 * `appcheck_outcome` tag and breadcrumb now carry what was actually seen.
 */
export class AuthTransportTimeoutError extends Error {
  readonly code = 'auth-transport/timeout';
  /** Which await expired — carried into the Sentry breadcrumb (AC-5). */
  readonly awaitName: string;
  readonly boundMs: number;

  constructor(awaitName: string, boundMs: number) {
    super(`${awaitName} did not settle within ${boundMs}ms`);
    this.name = 'AuthTransportTimeoutError';
    this.awaitName = awaitName;
    this.boundMs = boundMs;
  }
}

export function isAuthTransportTimeout(error: unknown): error is AuthTransportTimeoutError {
  return (error as { code?: string } | null)?.code === 'auth-transport/timeout';
}

/**
 * Thrown INSTEAD of attempting a sign-in while the page is inside App Check's
 * refusal throttle (KB-3352 AC-3). Every mint in this page fails at once there,
 * so Auth could only send a dummy token that enforced Identity Toolkit refuses —
 * waiting out `AUTH_REQUEST_BUDGET_MS` first would buy the user nothing.
 *
 * Like the timeout, its `code` avoids the `auth/` prefix so `handleFirebaseError`
 * cannot flatten it into "Authentication error: …".
 */
export class AuthAttestationRejectedError extends Error {
  readonly code = 'auth-transport/app-check-rejected';
  /** The call that was NOT made. */
  readonly awaitName: string;

  constructor(awaitName: string) {
    super(`${awaitName} was not attempted: App Check attestation was refused and this page is inside the SDK throttle`);
    this.name = 'AuthAttestationRejectedError';
    this.awaitName = awaitName;
  }
}

/**
 * Which `login()` failures become Sentry ERROR EVENTS (KB-3033 round-2 B3).
 *
 * Round-1 #2 correctly established that a breadcrumb nobody captures never
 * ships. The first fix over-corrected: it captured unconditionally, which turns
 * every mistyped password into an error event. `src/lib/sentry-init.ts` has no
 * `beforeSend`, no `ignoreErrors`, and no error sample rate — `tracesSampleRate: 0`
 * governs traces, not errors — so nothing downstream would have filtered them.
 * Typos are the single most common auth outcome, so the transport signal AC-5
 * exists to make attributable would have been buried under user-fault noise.
 *
 * These are the codes where the user did nothing wrong and the cause is ours or
 * the browser's. An error carrying NO code is captured too: an unrecognised
 * shape is exactly what we want to see. Everything else still breadcrumbs.
 *
 * It lives HERE rather than beside its one caller in `AuthContext.tsx` because
 * that is a component module: `react-refresh/only-export-components` permits a
 * literal constant export (`BOOTSTRAP_BUDGET_MS = 8000`) but not a computed one
 * like a `Set`, so exporting it there added a lint warning. This module already
 * owns the transport-failure-versus-user-failure distinction, which is exactly
 * the judgement this list encodes.
 */
export const SENTRY_CAPTURED_LOGIN_CODES: ReadonlySet<string> = new Set([
  // Browser/transport — the failure this ticket exists for.
  'auth/network-request-failed',
  'auth/internal-error',
  'auth/firebase-app-check-token-is-invalid',
  // OUR misconfiguration (round-3 optional 1). Without these the allowlist
  // filtered out the loudest possible failure: disable the Email/Password
  // provider in the Firebase console, or rotate/restrict the API key, and
  // EVERY login rejects with one of these codes — each carrying a code, none
  // in the set, so nothing was captured and the breadcrumb died in the buffer
  // with no event to attach to. A misconfiguration breaking 100% of logins
  // would have produced ZERO Sentry events, which is AC-5 defeated in the one
  // case where it matters most. The typo-noise fix that motivated this
  // allowlist is unaffected: these are our fault, not the user's.
  'auth/operation-not-allowed',
  'auth/invalid-api-key', // SECURITY_AUDIT_PASS: Firebase Auth error code, not a key
  'auth/quota-exceeded',
]);

/**
 * KB-2500 (M20), ported verbatim in behaviour: race `inner` against a bounded
 * timeout, ALWAYS clearing the timer once the race settles.
 *
 * The cleanup is the point. Without the `.finally`, a caller firing N requests
 * holds N live timers, each pinning its closure until it fires. The race itself
 * is otherwise ordinary — notably, `inner` is NOT cancelled when the timeout
 * wins (promises are not cancellable), so a late-settling `inner` simply has no
 * one listening.
 *
 * Timers are injected so tests can assert an actual outstanding-timer COUNT
 * rather than the mere absence of an error.
 */
export function raceWithBoundedTimeout<T>(
  inner: Promise<T>,
  timeoutValue: T,
  boundMs: number,
  setTimer: (cb: () => void, ms: number) => unknown = (cb, ms) => setTimeout(cb, ms),
  clearTimer: (handle: unknown) => void = (handle) => clearTimeout(handle as never),
): Promise<T> {
  let handle: unknown = null;
  return Promise.race([
    inner,
    new Promise<T>((resolve) => {
      handle = setTimer(() => resolve(timeoutValue), boundMs);
    }),
  ]).finally(() => clearTimer(handle));
}

/**
 * Module-private sentinel. Using a unique object (rather than `null`,
 * `undefined` or a string) means no legitimate resolution of `inner` can ever
 * be mistaken for a timeout — `signInWithEmailAndPassword` cannot return this.
 */
const TIMED_OUT = Symbol('auth-transport/timed-out');

/**
 * Bound an auth await so it can never hang, REJECTING on expiry.
 *
 * `raceWithBoundedTimeout` resolves with a sentinel value rather than
 * rejecting, which is right for its marketing callers (they degrade to `null`).
 * Auth callers need the opposite: a rejection, so `handleLogin`'s `finally`
 * resets the button and its `catch` can render an actionable error. Rather than
 * hand-roll a second race — which KB-3033 AC-3 explicitly forbids — this races
 * through the ported helper with a private sentinel and converts the sentinel
 * into a throw.
 */
export async function boundAuthCall<T>(
  inner: Promise<T>,
  awaitName: string,
  boundMs: number = AUTH_REQUEST_BUDGET_MS,
  setTimer?: (cb: () => void, ms: number) => unknown,
  clearTimer?: (handle: unknown) => void,
): Promise<T> {
  const result = await raceWithBoundedTimeout<T | typeof TIMED_OUT>(
    inner,
    TIMED_OUT,
    boundMs,
    setTimer,
    clearTimer,
  );
  if (result === TIMED_OUT) {
    throw new AuthTransportTimeoutError(awaitName, boundMs);
  }
  return result;
}

/**
 * KB-3352: the copy for a failed sign-in is chosen by the App Check attestation
 * outcome (`src/lib/appCheckOutcome.ts`), and each string asserts only what its
 * outcome evidences.
 *
 * Before KB-3352 every timeout said the reCAPTCHA check was "most likely
 * blocked" and prescribed turning off extensions and content blockers, whatever
 * had actually happened — nothing a timeout observes evidences that. When the
 * stall was something else (an attestation refused with HTTP 403, whose 24-hour
 * SDK throttle makes every retry in the tab fail; a slow mint; a request that
 * never answered) the copy still sent the reader hunting for a blocker. A
 * reporter followed it and concluded their browser was broken. Which mechanism
 * stalled THEIR sign-ins is not established, which is exactly why the
 * unattributed copy below names none.
 *
 * So, per outcome:
 *   - `rejected` — a refusal still inside the SDK's throttle window, the one
 *     state in which retrying in this tab CANNOT help. Never promises an
 *     automatic sign-in.
 *
 *     Identity Toolkit refusing the token (`auth/firebase-app-check-token-is-invalid`)
 *     is not, on its own, a `rejected` outcome; it renders the neutral
 *     `AUTH_ERROR_COPY.APP_CHECK_TOKEN_INVALID`. While the SDK held a valid
 *     token, Google had minted that token for this very session, and what is
 *     left to refuse it — an App Check app, project or enforcement mismatch —
 *     does not depend on the browser (round-2 B4). While it held none, Auth sent
 *     a placeholder (round-1 B2). Neither evidences "refused this browser session".
 *   - `script-unavailable` — `window.grecaptcha` is absent. The only outcome in
 *     which a blocker diagnosis is evidenced, so the only one that names one.
 *   - everything else (`pending`, `ok`, `inactive`) — NO cause is named. Three
 *     different mechanisms produce a 15 s stall and nothing the page holds can
 *     tell them apart.
 *
 * Each must stay DISTINGUISHABLE from `AUTH_ERROR_COPY.NETWORK_REQUEST_FAILED`
 * (`src/utils/errorUtils.ts`): a request that was refused and one we stopped
 * waiting on are different facts. And none may say the request "was never
 * sent" — expiring a race proves only that WE stopped waiting, never that the
 * SDK did (KB-3033 round 1), which is why `login()` still reconciles a late
 * settle.
 *
 * `tests/unit/auth/errorCopy.distinct.test.ts` pins that they never converge.
 */

/**
 * `rejected`, inside the SDK's refusal throttle. Shared by both doors: neither
 * can sign in. It names support as well as another browser, because an exchange
 * 403 or 404 can also be a configuration fault that no browser change fixes
 * (round-1 O3).
 */
export const AUTH_ATTESTATION_REJECTED_MESSAGE =
  "Sign-in is blocked in this tab: Google's security check refused this browser session, so " +
  'trying again here will not work. Reload the page to run a fresh check. If it is refused ' +
  'again, use a different browser or browser profile, or contact support@kidsbooked.com.';

/**
 * Email timeout, `script-unavailable`. Keeps the automatic-sign-in promise: if
 * the script loads late, the pending sign-in can still settle and be reconciled.
 */
export const AUTH_SCRIPT_UNAVAILABLE_TIMEOUT_MESSAGE =
  'Sign-in timed out: the reCAPTCHA security check did not load in this browser, which is ' +
  'usually an extension, a content blocker, or a network that blocks Google. Turn off ' +
  'extensions and content blockers for this site and reload — in Chrome or Firefox, a private ' +
  'window is a quick way to test that. If the sign-in does go through on its own, you will be ' +
  'signed in automatically.';

/**
 * Email timeout whose cause the page cannot evidence (`pending`, `ok`,
 * `inactive`). Names no cause. The automatic-sign-in promise stays true here
 * because the late-settle reconciler in `AuthContext.login` can still deliver it.
 */
export const AUTH_TIMEOUT_USER_MESSAGE =
  "Sign-in timed out: we stopped waiting for it to finish, and we can't tell what held it up. " +
  'Reload the page and try again. If the sign-in does go through on its own, you will be signed ' +
  'in automatically.';

/**
 * The GOOGLE POPUP timeouts (KB-3033 round-2 B4). Neither can reuse an email
 * string: on this path a late settle is signed OUT, because it would otherwise
 * skip the allowlist gate in `LoginPage.handleGoogleLogin`. So each states the
 * end state that sign-out enforces — the attempt was cancelled — and never the
 * email path's automatic-sign-in promise.
 */
export const AUTH_POPUP_SCRIPT_UNAVAILABLE_TIMEOUT_MESSAGE =
  'Google sign-in timed out: the reCAPTCHA security check did not load in this browser, which ' +
  'is usually an extension, a content blocker, or a network that blocks Google. The attempt was ' +
  'cancelled, so you have not been signed in. Turn off extensions and content blockers for this ' +
  'site, then reload the page and try again, or use a different browser.';

export const AUTH_POPUP_TIMEOUT_USER_MESSAGE =
  "Google sign-in timed out: we stopped waiting for it to finish, and we can't tell what held " +
  'it up. The attempt was cancelled, so you have not been signed in. Reload the page and try ' +
  'again.';

export type AuthDoor = 'email' | 'google';

export function authFailureAttestationMessage(outcome: AppCheckOutcome, door: AuthDoor): string {
  if (outcome === 'rejected') {
    return AUTH_ATTESTATION_REJECTED_MESSAGE;
  }
  if (outcome === 'script-unavailable') {
    return door === 'email'
      ? AUTH_SCRIPT_UNAVAILABLE_TIMEOUT_MESSAGE
      : AUTH_POPUP_SCRIPT_UNAVAILABLE_TIMEOUT_MESSAGE;
  }
  return door === 'email' ? AUTH_TIMEOUT_USER_MESSAGE : AUTH_POPUP_TIMEOUT_USER_MESSAGE;
}
